import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useCallback, useEffect, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import DefaultPanelRows from '../components/DefaultPanelRows/DefaultPanelRows';
import { DefaultPanelItem } from '../model';
import defaultPanelService from '../service';

function DefaultPanel() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [hasFetched, setHasFetched] = useState(false);
  const [defaultPanels, setDefaultPanel] = useState<DefaultPanelItem[]>([]);

  // form setup
  const schema = yup.object().shape(
    defaultPanels.reduce((acc: Record<string, yup.StringSchema>, item) => {
      const fieldName = `image_${item.id}`;
      acc[fieldName] = yup.string().required(t('DefaultPanel.warn_field_required'));
      return acc;
    }, {})
  );

  const defaultValues = defaultPanels.reduce((acc, item) => {
    acc[`image_${item.id}`] = item.image || '';
    return acc;
  }, {} as Record<string, any>);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Partial<DefaultPanelItem>>({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: 'onSubmit',
  });

  // fetch data
  const fetchData = async () => {
    try {
      const response = await defaultPanelService.getList();
      if (response.data) {
        setDefaultPanel(response.data);
      } else {
        console.error('Failed to fetch default panel data');
      }
    } catch (error) {
      console.error('Error fetching default panel data:', error);
    }
  };

  useEffect(() => {
    if (defaultPanels.length > 0) {
      const fetchedDefaultValues = defaultPanels.reduce((acc, item) => {
        acc[`image_${item.id}`] = item.image || '';
        return acc;
      }, {} as Record<string, any>);

      reset(fetchedDefaultValues);
    }
  }, [defaultPanels, reset]);

  useEffect(() => {
    if (!hasFetched) {
      fetchData().then(() => setHasFetched(true));
    }
  }, [hasFetched]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSave = useCallback(
    handleSubmit(
      (formData) => {
        try {
          const updatedData: DefaultPanelItem[] = defaultPanels.map((item) => {
            const fieldName = `image_${item.id}`;
            const newImagePath = formData[fieldName as keyof typeof formData] as string;

            return {
              ...item,
              image: newImagePath,
            };
          });
          defaultPanelService.updateList(updatedData).then((response) => {
            if (response.data) {
              setDefaultPanel(response.data);
              showGlobalModal(() => <AlertModal title={t('App.success')} message={t('DefaultPanel.success_save')} />);
            } else {
              console.error('Failed to update default panel data');
            }
          });
        } catch (error) {
          console.error('Error updating default panel data:', error);
        }
      },
      () => {
        showGlobalModal(() => (
          <AlertModal title={t('App.warning')} message={t('DefaultPanel.image_validation_error')} />
        ));
      }
    ),
    [defaultPanels, handleSubmit, showGlobalModal, t]
  );
  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('DefaultPanel.title')} />
          </div>

          <form onSubmit={handleSave}>
            <div className="d-flex flex-column justify-content-center align-items-center ">
              <div className="text-end" style={{ width: '700px', marginTop: '10px' }}>
                <Button className="btn btn--dark-green" onClick={handleSave}>
                  {t('DefaultPanel.save_button')}
                </Button>
              </div>
              <div className="sticky-table" style={{ width: '700px', marginTop: '10px' }}>
                <Table bordered style={{ borderTop: '1px solid #dee2e6' }}>
                  <tbody>
                    {defaultPanels.map((item) => (
                      <DefaultPanelRows key={item.id} item={item} control={control} errors={errors} />
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DefaultPanel;
